import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { PopupHttpErrors } from 'src/app/decorators/http-error/popup-http-errors.decorator';
import { ECourseRequestType } from 'src/app/shared/enums/course-request-type.enum';
import { ICourseAttribute } from 'src/app/shared/models/course-attribute.model';
import { ICourseSearchResult } from 'src/app/shared/models/course-search.model';
import { ICourseSection } from 'src/app/shared/models/course-section.model';
import { ICourse } from 'src/app/shared/models/course.model';
import { IHistoryCourse } from 'src/app/shared/models/history-course.model';
import { IOlrRegistrationSummary } from 'src/app/shared/models/olr-registration-summary.model';
import { ITerm } from 'src/app/shared/models/term.model';
import { IBasePerson } from 'src/app/shared/people/models/person';
import { Cacheable } from 'ts-cacheable';
import { CourseRepository } from '../repositories/course.repository';
import { ICourseInfo } from 'src/app/shared/models/course-info.model';

@Injectable({
  providedIn: 'root'
})
export class CourseService {

  constructor(
    private courseRepo: CourseRepository
  ) { }

  @PopupHttpErrors()
  @Cacheable()
  public getCourses(params: {
    crn?: string,
    subjectCode?: string, 
    courseNumber?: string,
    sectionNumber?: string,
    subterm?: string,
    campusCode?: string,
    termCode?: string
  }): Observable<Array<ICourseSearchResult>> {
    const fullParams = {
      ...params,
      type: ECourseRequestType.GENERAL_SEARCH
    };

    return this.courseRepo.getCourses(fullParams).pipe(
      map(res => res.searchResults ?? [])
    );
  }

  @PopupHttpErrors()
  @Cacheable()
  public getCourseByCrn(
    crn?: string,
    termCode?: string
  ): Observable<ICourseSection> {
    const fullParams = {
      crn,
      termCode,
      type: ECourseRequestType.BY_CRN
    };

    return this.courseRepo.getCourses(fullParams).pipe(
      map(res => res.courseSections[0])
    );
  }

  @PopupHttpErrors()
  @Cacheable()
  public getStudentCourses(pidm: number, termCode: string, campusCode?: string): Observable<Array<ICourse>> {
    const fullParams = {
      pidm,
      termCode,
      campusCode,
      type: ECourseRequestType.STUDENT_COURSES
    };
    
    return this.courseRepo.getCourses(fullParams).pipe(
      map(res => res.courseRegistrations ?? [])
    );
  }

  @PopupHttpErrors()
  @Cacheable({ maxCacheCount: 20 })
  public getTaughtSections(termCode: string, pidm?: number): Observable<Array<ICourseSection>> {
    const fullParams = {
      pidm,
      termCode,
      type: ECourseRequestType.FACULTY_COURSES
    };
    
    return this.courseRepo.getCourses(fullParams).pipe(
      map(res => res.courseSections ?? [])
    );
  }

  @PopupHttpErrors()
  @Cacheable()
  public getStudentCserAppealEligibleCourses(pidm: number): Observable<Array<IHistoryCourse>> {
    const fullParams = {
      pidm,
      type: ECourseRequestType.CSER_APPEAL
    };
    
    return this.courseRepo.getCourses(fullParams).pipe(
      map(res => res.historyCourses ?? [])
    );
  }

  @PopupHttpErrors()
  @Cacheable()
  public getStudentExtensionEligibleCourses(pidm: number, termCode: string): Observable<Array<ICourse>> {
    const fullParams = {
      pidm,
      termCode,
      type: ECourseRequestType.LUOA_EXTENSION
    };
    
    return this.courseRepo.getCourses(fullParams).pipe(
      map(res => res.courseRegistrations ?? [])
    );
  }

  @PopupHttpErrors()
  @Cacheable()
  public getStudentFnAppealableCourses(pidm: number, termCode: string): Observable<Array<ICourse>> {
    const fullParams = {
      pidm,
      termCode,
      type: ECourseRequestType.LUOA_APPEAL
    };
    
    return this.courseRepo.getCourses(fullParams).pipe(
      map(res => res.courseRegistrations ?? [])
    );
  }

  @PopupHttpErrors()
  @Cacheable()
  public getRegistrations(sectionId: number): Observable<Array<IBasePerson>> {
    return this.courseRepo.getRegistrations(sectionId);
  }

  @PopupHttpErrors()
  @Cacheable({ maxCacheCount: 20 })
  public getTerms(pidm?: number, allTerms?: boolean, luoaOnly?: boolean, faculty?: boolean): Observable<Array<ITerm>> {
    return this.courseRepo.terms(pidm, allTerms, luoaOnly, faculty);
  }

  @PopupHttpErrors()
  @Cacheable()
  public findOlrRegistrationSummary(pidm: number, crn: string, termCode: string): Observable<IOlrRegistrationSummary> {
    return this.courseRepo.findOlrRegistrationSummary(pidm, crn, termCode);
  }

  @PopupHttpErrors()
  @Cacheable()
  public getCourseAttributes(crn: string, termCode: string): Observable<Array<ICourseAttribute>> {
    return this.courseRepo.getCourseAttributes(crn, termCode);
  }

  @PopupHttpErrors()
  @Cacheable()
  public getCourseInfo(crn: string, termCode: string): Observable<ICourseInfo> {
    return this.courseRepo.getCourseInfo(crn, termCode);
  }
  
}
